import { getDocuments, updateDocument } from '../services/documents';
import { TIMEZONE_GMT } from '../services/request';

export const getChangeAvailability = async (mode: string): Promise<any> => {
  const data = getDocuments(`operations/${mode}/change_availability`);
  return data;
};

export const getRemoteStartTransaction = async (mode: string): Promise<any> => {
  const data = getDocuments(`operations/${mode}/remote_start_transaction`);
  return data;
};

export const getRemoteStopTransaction = async (mode: string): Promise<any> => {
  const data = getDocuments(`operations/${mode}/remote_stop_transaction`);
  return data;
};

export const getReset = async (mode: string): Promise<any> => {
  const data = getDocuments(`operations/${mode}/reset`);
  return data;
};

export const getGetConfiguration = async (mode: string): Promise<any> => {
  const data = getDocuments(`operations/${mode}/get_configuration`);
  return data;
};

export const getChangeConfiguration = async (mode: string): Promise<any> => {
  const data = getDocuments(`operations/${mode}/change_configuration`);
  return data;
};

export const getClearCache = async (mode: string): Promise<any> => {
  const data = getDocuments(`operations/${mode}/clear_cache`);
  return data;
};

export const getUnlockConnector = async (mode: string): Promise<any> => {
  const data = getDocuments(`operations/${mode}/unlock_connector`);
  return data;
};

export const getUpdateFirmware = async (mode: string): Promise<any> => {
  const data = getDocuments(`operations/${mode}/update_firmware`);
  return data;
};

export const getDataTransfer = async (mode: string): Promise<any> => {
  const data = getDocuments(`operations/${mode}/data_transfer`);
  return data;
};

export const getGetDiagnostics = async (mode: string): Promise<any> => {
  const data = getDocuments(`operations/${mode}/get_diagnostics`);
  return data;
};

export interface GetChargeboxTaskParams {
  chargeBoxId: string;
  taskId: number;
}

export const getChargeboxTask = async (
  params: GetChargeboxTaskParams,
): Promise<any> => {
  const data = getDocuments(
    `operations/tasks/${params.taskId}/details/${params.chargeBoxId}`,
  );
  return data;
};

export const getTasks = async (): Promise<any> => {
  const data = getDocuments(`operations/tasks?timeZone=${TIMEZONE_GMT}`);
  return data;
};

export const getTriggerMessage = async (mode: string): Promise<any> => {
  const data = getDocuments(`operations/${mode}/trigger_message`);
  return data;
};

export const getSetChargingProfile = async (mode: string): Promise<any> => {
  const data = getDocuments(`operations/${mode}/set_charging_profile`);
  return data;
};

export const getClearChargingProfile = async (mode: string): Promise<any> => {
  const data = getDocuments(`operations/${mode}/clear_charging_profile`);
  return data;
};

export const getSendLocalList = async (mode: string): Promise<any> => {
  const data = getDocuments(`operations/${mode}/send_local_list`);
  return data;
};

export const getGetCompositeSchedule = async (mode: string): Promise<any> => {
  const data = getDocuments(`operations/${mode}/get_composite_schedule`);
  return data;
};

export const getReserveNow = async (mode: string): Promise<any> => {
  const data = getDocuments(`operations/${mode}/reserve_now`);
  return data;
};

export interface SetChargingProfileParams {
  chargePointSelectList: ChargePointSelect[];
  chargingProfilePk: number;
  connectorId: number;
  mode: string;
}

export const setSetChargingProfile = async (
  params: SetChargingProfileParams,
): Promise<any> => {
  const data = await updateDocument(
    `${params.mode}/set_charging_profile`,
    params,
    'operations',
  );
  return data;
};

export interface ClearChargingProfileParams {
  chargePointSelectList: ChargePointSelect[];
  filterType: string;
  chargingProfilePurpose: string;
  stackLevel: number | null;
  chargingProfilePk: number;
  connectorId: number;
  mode: string;
}

export const setClearChargingProfile = async (
  params: ClearChargingProfileParams,
): Promise<any> => {
  const data = await updateDocument(
    `${params.mode}/clear_charging_profile`,
    params,
    'operations',
  );
  return data;
};

export interface ChargePointSelect {
  chargeBoxId: string;
  endpointAddress: string;
  endpointAddressSet: boolean;
  ocppTransport: string;
  soap: boolean;
}

export interface ChangeAvailabilityParams {
  chargePointSelectList: ChargePointSelect[];
  availType: string;
  connectorId: number;
  mode: string;
}

export const setChangeAvailability = async (
  params: ChangeAvailabilityParams,
): Promise<any> => {
  const data = await updateDocument(
    `${params.mode}/change_availability`,
    params,
    'operations',
  );
  return data;
};

export interface RemoteStartTransactionParams {
  chargePointSelectList: ChargePointSelect[];
  connectorId: number;
  idTag: string;
  paymentType: string;
  mode: string;
  directPayment: boolean;
  cardUuid: string | null;
}

export const setRemoteStartTransaction = async (
  params: RemoteStartTransactionParams,
): Promise<any> => {
  const data = await updateDocument(
    `${params.mode}/remote_start_transaction`,
    params,
    'operations',
  );
  return data;
};

export interface RemoteStopTransactionParams {
  chargePointSelectList: ChargePointSelect[];
  transactionId: number;
  mode: string;
}

export const setRemoteStopTransaction = async (
  params: RemoteStopTransactionParams,
): Promise<any> => {
  const data = await updateDocument(
    `${params.mode}/remote_stop_transaction`,
    params,
    'operations',
  );
  return data;
};

export interface ResetParams {
  chargePointSelectList: ChargePointSelect[];
  resetType: 'HARD';
  mode: string;
}

export const setReset = async (params: ResetParams): Promise<any> => {
  const data = await updateDocument(
    `${params.mode}/reset`,
    params,
    'operations',
  );
  return data;
};

export interface GetConfigurationParams {
  chargePointSelectList: ChargePointSelect[];
  confKeyList: string[];
  setConfKeyList: boolean;
  mode: string;
}

export const setGetConfiguration = async (
  params: GetConfigurationParams,
): Promise<any> => {
  const data = await updateDocument(
    `${params.mode}/get_configuration`,
    params,
    'operations',
  );
  return data;
};

export interface ChangeConfigurationParams {
  chargePointSelectList: ChargePointSelect[];
  keyType: string;
  confKey: string;
  customConfKey: string;
  value: string;
  mode: string;
}

export const setChangeConfiguration = async (
  params: ChangeConfigurationParams,
): Promise<any> => {
  const data = await updateDocument(
    `${params.mode}/change_configuration`,
    params,
    'operations',
  );
  return data;
};

export interface UnlockConnectorParams {
  chargePointSelectList: ChargePointSelect[];
  connectorId: number;
  mode: string;
}

export const setUnlockConnector = async (
  params: UnlockConnectorParams,
): Promise<any> => {
  const data = await updateDocument(
    `${params.mode}/unlock_connector`,
    params,
    'operations',
  );
  return data;
};

export interface UpdateFirmwareParams {
  chargePointSelectList: ChargePointSelect[];
  location: string;
  retries: number;
  retryInterval: number;
  retrieve: string;
  mode: string;
}

export const setUpdateFirmware = async (
  params: UpdateFirmwareParams,
): Promise<any> => {
  const data = await updateDocument(
    `${params.mode}/update_firmware`,
    params,
    'operations',
  );
  return data;
};

export interface DataTransferParams {
  chargePointSelectList: ChargePointSelect[];
  data: string;
  messageId: string;
  vendorId: string;
  mode: string;
}

export const setDataTransfer = async (
  params: DataTransferParams,
): Promise<any> => {
  const data = await updateDocument(
    `${params.mode}/data_transfer`,
    params,
    'operations',
  );
  return data;
};

export interface TriggerMessageParams {
  chargePointSelectList: ChargePointSelect[];
  triggerMessage: string;
  connectorId: number;
  mode: string;
}

export const setTriggerMessage = async (
  params: TriggerMessageParams,
): Promise<any> => {
  const data = await updateDocument(
    `${params.mode}/trigger_message`,
    params,
    'operations',
  );
  return data;
};

export interface GetDiagnosticsParams {
  chargePointSelectList: ChargePointSelect[];
  location: string;
  retries: number;
  retryInterval: number;
  start: string | null;
  stop: string | null;
  mode: string;
}

export const setGetDiagnostics = async (
  params: GetDiagnosticsParams,
): Promise<any> => {
  const data = await updateDocument(
    `${params.mode}/get_diagnostics`,
    params,
    'operations',
  );
  return data;
};

export interface ClearCacheParams {
  chargePointSelectList: ChargePointSelect[];
  mode: string;
}

export const setClearCache = async (params: ClearCacheParams): Promise<any> => {
  const data = await updateDocument(
    `${params.mode}/clear_cache`,
    params,
    'operations',
  );
  return data;
};

export type SendLocalListUpdateType = 'DIFFERENTIAL' | 'FULL';

export interface SendLocalListParams {
  chargePointSelectList: ChargePointSelect[];
  addUpdateList: string[];
  deleteList: string[];
  listVersion: number;
  updateType: SendLocalListUpdateType;
  mode: string;
}

export const setSendLocalList = async (params: SendLocalListParams): Promise<any> => {
  const data = await updateDocument(
    `${params.mode}/send_local_list`,
    params,
    'operations',
  );
  return data;
};

export type ChargingRateUnit = 'A' | 'W';

export interface GetCompositeScheduleParams {
  chargePointSelectList: ChargePointSelect[];
  connectorId: number;
  durationInSeconds: number;
  chargingRateUnit?: ChargingRateUnit;
  mode: string;
}

export const setGetCompositeSchedule = async (
  params: GetCompositeScheduleParams,
): Promise<any> => {
  const data = await updateDocument(
    `${params.mode}/get_composite_schedule`,
    params,
    'operations',
  );
  return data;
};

export interface ReserveNowParams {
  chargePointSelectList: ChargePointSelect[];
  connectorId: number;
  expiryMinutes: number;
  idTag: string;
  mode: string;
}

export const setReserveNow = async (params: ReserveNowParams): Promise<any> => {
  const data = await updateDocument(
    `${params.mode}/reserve_now`,
    params,
    'operations',
  );
  return data;
};
